import {
  streamCodeSymbols,
  buildMessages,
  parseSimoneResponse,
  WORKSPACE_SYSTEM_PROMPT,
  resolveModel
} from '@symbo-ls/simone-server'

// FA204 / frank trap — HISTORY_KEY/loadHistory/saveHistory used to live at
// module scope, referenced bare from clearSession/giveMeAnswer below.
// Each export now carries its own self-contained inline copy.

export function clearSession() {
  const HISTORY_KEY = 'ai_simone_history'
  try {
    localStorage.removeItem(HISTORY_KEY)
  } catch (_) {
    /* ignore */
  }
}

export async function giveMeAnswer(text, images, selectedModel) {
  // Local thread cache — Simone is stateless, so we replay history
  // client-side. Self-contained inline copy, see clearSession above.
  const HISTORY_KEY = 'ai_simone_history'
  function loadHistory() {
    try {
      const raw = localStorage.getItem(HISTORY_KEY)
      if (!raw) return []
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : []
    } catch (_) {
      return []
    }
  }
  function saveHistory(history) {
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(-20)))
    } catch (_) {
      /* quota — drop */
    }
  }

  const cleanData = await this.call('getCleanCanvasScope', this.getCanvasScope())
  const context = Object.fromEntries(
    Object.entries(cleanData).filter(([_, value]) => value !== undefined && value !== null)
  )

  const selectedResource = this.getChosenKey() || this.getSelectedKey()
  if (selectedResource) context.selectedResource = selectedResource
  const selectedFunction = this.getCanvasScope().activeFunction
  if (selectedFunction) context.selectedFunction = selectedFunction

  if (images && images.length > 0) {
    console.warn('[simone] image attachments not supported by /api/code-symbols — dropping')
  }

  const history = loadHistory()
  const messages = buildMessages({
    system: WORKSPACE_SYSTEM_PROMPT,
    history,
    user: text,
    context
  })

  try {
    const { text: answer, errors } = await streamCodeSymbols(messages, {
      model: resolveModel(selectedModel)
    })
    if (errors && errors.length) {
      console.error('[simone] stream errors:', errors)
    }
    history.push({ role: 'user', content: text })
    history.push({ role: 'assistant', content: answer })
    saveHistory(history)
    return parseSimoneResponse(answer)
  } catch (error) {
    console.error('[simone] giveMeAnswer failed:', error)
    return {
      kind: 'error',
      message: this.call('polyglot', 'ai.chat.unreachable').replace('{error}', error.message)
    }
  }
}

export function parseAIAnswer(parsed) {
  if (!parsed) {
    return { message: this.call('polyglot', 'ai.chat.noResponse') }
  }
  switch (parsed.kind) {
    case 'asset':
      return {
        asset: parsed.asset,
        type: (parsed.asset.type || '') + 's',
        message: parsed.message
      }
    case 'multi':
      return {
        asset: parsed.assets,
        multiple: true,
        message: parsed.message
      }
    case 'component':
      return {
        asset: { type: 'component', value: parsed.code, description: parsed.text },
        message: parsed.message
      }
    case 'error':
      return { message: parsed.message }
    case 'text':
    default:
      return { message: parsed.text || parsed.message || '' }
  }
}

export function animateMultiple(changes) {
  const jointData = []
  changes.forEach((item, i) => {
    if (i % 2 === 0) {
      const [, , value] = item
      jointData.push({ value })
    } else {
      const [, , schema] = item
      const currentItem = jointData[jointData.length - 1]
      jointData[jointData.length - 1] = { ...currentItem, ...schema }
    }
  })
  jointData.forEach(async (item, i) => {
    // const t = setTimeout(async () => {
    // await this.addComponentOnCanvas(item)
    // clearTimeout(t)
    // }, i * 350)
  })
}

export async function setPrompt(msg) {
  const AIMessage = this.getFilesSidebarPrompt().Label.Span
  await AIMessage.state.update({
    keyword: msg
  })
  const t = setTimeout(() => {
    AIMessage.update({
      html: AIMessage.state.keyword + '&nbsp;'
    })
    AIMessage.node.focus()
    const t2 = setTimeout(() => {
      // select all the content in the element
      document.execCommand('selectAll', false, null)
      // collapse selection to the end
      document.getSelection().collapseToEnd()
      clearTimeout(t2)
    }, 35)
    clearTimeout(t)
  }, 35)
}

// Message type handlers
export function handleGreetingResponse(responseData) {
  return {
    flow: 'y',
    gap: 'A',
    padding: 'Y2 A',
    shape: 'bubble',
    shapeDirection: 'top right',
    round: 'C X C C',
    theme: 'dialog',
    maxWidth: 'I1',
    children: [
      {
        extends: 'P',
        text: responseData.message,
        fontWeight: '500'
      },
      {
        extends: 'P',
        text: responseData.capabilities_summary,
        margin: 'A 0 Z',
        color: 'caption'
      },
      ...responseData.categories.map((category) => ({
        flow: 'y',
        gap: 'Z1',
        children: [
          {
            extends: 'H6',
            text: category.name,
            fontSize: 'Z1',
            margin: '0 0 X'
          },
          {
            flow: 'y',
            gap: 'X',
            children: category.items.map((item) => ({
              extends: 'P',
              text: `- ${item}`,
              color: 'caption',
              fontSize: 'Z'
            }))
          }
        ]
      }))
    ]
  }
}

export function handleTextResponse(content) {
  // FA204 / frank trap — self-contained inline copies of formatCodeBlock/
  // formatTextParts, see those exports below for the canonical shape.
  function _formatCodeBlock(part) {
    const code = part.replace(/```(js|javascript)?\n/, '').replace(/```$/, '')
    return {
      extends: 'P',
      text: code,
      theme: 'document',
      padding: 'A',
      round: 'radiusControl',
      fontSize: 'Z',
      fontFamily: 'monospace',
      margin: 'A 0',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-all'
    }
  }
  function _formatTextParts(part) {
    return part
      .split(/(### .*)/g)
      .filter((p) => p)
      .map((textPart) => {
        if (textPart.startsWith('### ')) {
          return {
            extends: 'H6',
            text: textPart.substring(4),
            margin: 'A 0 Z',
            fontWeight: 'bold'
          }
        }
        return {
          extends: 'P',
          text: textPart,
          color: 'caption',
          fontSize: 'Z',
          lineHeight: '1.5'
        }
      })
  }

  const parts = content.split(/(```(?:js|javascript)?\n[\s\S]*?```)/g)
  const children = parts
    .filter((part) => part)
    .map((part) => {
      if (part.startsWith('```')) {
        return _formatCodeBlock(part)
      }
      return _formatTextParts(part)
    })
    .flat()

  return {
    flow: 'y',
    gap: 'A',
    padding: 'Y2 A',
    shape: 'bubble',
    shapeDirection: 'top right',
    round: 'C X C C',
    theme: 'dialog',
    maxWidth: '85%',
    children
  }
}

export function handleCodeResponse(message) {
  // FA204 / frank trap — self-contained inline copies of
  // sanitizeComponentCode/buildCodeResponseUI (which itself needs its own
  // copies of formatTextParts/formatCodeBlock — see those exports below).
  function _sanitizeComponentCode(code) {
    return code.replace(/(}\s*)\]/g, '$1}').replace(/,\s*([}\]])/g, '$1')
  }
  function _buildCodeResponseUI(analysis_text, component_code) {
    function _formatCodeBlock(part) {
      const code = part.replace(/```(js|javascript)?\n/, '').replace(/```$/, '')
      return {
        extends: 'P',
        text: code,
        theme: 'document',
        padding: 'A',
        round: 'radiusControl',
        fontSize: 'Z',
        fontFamily: 'monospace',
        margin: 'A 0',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all'
      }
    }
    function _formatTextParts(part) {
      return part
        .split(/(### .*)/g)
        .filter((p) => p)
        .map((textPart) => {
          if (textPart.startsWith('### ')) {
            return {
              extends: 'H6',
              text: textPart.substring(4),
              margin: 'A 0 Z',
              fontWeight: 'bold'
            }
          }
          return {
            extends: 'P',
            text: textPart,
            color: 'caption',
            fontSize: 'Z',
            lineHeight: '1.5'
          }
        })
    }
    function _buildAnalysisSection(analysis_text) {
      return {
        flow: 'y',
        children: _formatTextParts(analysis_text)
      }
    }
    function _buildCodeSection(component_code) {
      return _formatCodeBlock(component_code)
    }
    try {
      return {
        flow: 'x',
        contentEditable: false,
        shape: 'bubble',
        shapeDirection: 'top right',
        round: 'C X C C',
        theme: 'dialog',
        padding: 'Y2 A',
        margin: '- -Z',
        maxWidth: '85%',
        children: [
          analysis_text && _buildAnalysisSection(analysis_text),
          component_code && _buildCodeSection(component_code)
        ].filter(Boolean)
      }
    } catch (e) {
      console.error('Failed to parse Symbols component:', e)
      return {
        extends: 'P',
        text: this.call('polyglot', 'ai.chat.renderError').replace('{error}', e.message)
      }
    }
  }

  const { analysis_text, symbols_code_response, type } = message.code_response || {}
  let component_code = symbols_code_response
    ? symbols_code_response.component_code
    : type === 'symbols_component_code'
      ? message.code_response.component_code
      : ''

  if (component_code) {
    component_code = _sanitizeComponentCode(component_code)
  }

  if (!component_code && !analysis_text) {
    return {
      extends: 'P',
      text: '{{ ai.chat.invalidCode | polyglot }}'
    }
  }

  return _buildCodeResponseUI(analysis_text, component_code)
}

// Helper functions
export function formatCodeBlock(part) {
  const code = part.replace(/```(js|javascript)?\n/, '').replace(/```$/, '')
  return {
    extends: 'P',
    text: code,
    theme: 'document',
    padding: 'A',
    round: 'radiusControl',
    fontSize: 'Z',
    fontFamily: 'monospace',
    margin: 'A 0',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all'
  }
}

export function formatTextParts(part) {
  return part
    .split(/(### .*)/g)
    .filter((p) => p)
    .map((textPart) => {
      if (textPart.startsWith('### ')) {
        return {
          extends: 'H6',
          text: textPart.substring(4),
          margin: 'A 0 Z',
          fontWeight: 'bold'
        }
      }
      return {
        extends: 'P',
        text: textPart,
        color: 'caption',
        fontSize: 'Z',
        lineHeight: '1.5'
      }
    })
}

export function sanitizeComponentCode(code) {
  return code.replace(/(}\s*)\]/g, '$1}').replace(/,\s*([}\]])/g, '$1')
}

// `buildAnalysisSection`/`buildCodeSection` were referenced (never defined)
// by `buildCodeResponseUI` below — a real `ReferenceError` on every call
// with `analysis_text` set, silently downgraded to the "Error rendering
// component" fallback by the surrounding try/catch (frank `to-json`: 2
// `unresolvable-scope-read` findings). Zero live callers of
// `buildCodeResponseUI`/`handleCodeResponse` exist anywhere in the project
// today, so this was never exercised — implemented using the file's own
// existing formatters (`formatTextParts` for the analysis prose,
// `formatCodeBlock` for the code), matching the naming's evident intent.
//
// FA204 / frank trap — `buildAnalysisSection`/`buildCodeSection` are now
// nested INSIDE buildCodeResponseUI's own body instead of at module scope
// (nested declarations inside the exported function's own source are never
// rewritten by frank and keep working via normal JS closures), and each
// calls its own self-contained inline copy of formatTextParts/
// formatCodeBlock rather than the bare sibling exports below.
export function buildCodeResponseUI(analysis_text, component_code) {
  function _formatCodeBlock(part) {
    const code = part.replace(/```(js|javascript)?\n/, '').replace(/```$/, '')
    return {
      extends: 'P',
      text: code,
      theme: 'document',
      padding: 'A',
      round: 'radiusControl',
      fontSize: 'Z',
      fontFamily: 'monospace',
      margin: 'A 0',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-all'
    }
  }
  function _formatTextParts(part) {
    return part
      .split(/(### .*)/g)
      .filter((p) => p)
      .map((textPart) => {
        if (textPart.startsWith('### ')) {
          return {
            extends: 'H6',
            text: textPart.substring(4),
            margin: 'A 0 Z',
            fontWeight: 'bold'
          }
        }
        return {
          extends: 'P',
          text: textPart,
          color: 'caption',
          fontSize: 'Z',
          lineHeight: '1.5'
        }
      })
  }
  function buildAnalysisSection(analysis_text) {
    return {
      flow: 'y',
      children: _formatTextParts(analysis_text)
    }
  }

  function buildCodeSection(component_code) {
    return _formatCodeBlock(component_code)
  }

  try {
    return {
      flow: 'x',
      contentEditable: false,
      shape: 'bubble',
      shapeDirection: 'top right',
      round: 'C X C C',
      theme: 'dialog',
      padding: 'Y2 A',
      margin: '- -Z',
      maxWidth: '85%',
      children: [
        analysis_text && buildAnalysisSection(analysis_text),
        component_code && buildCodeSection(component_code)
      ].filter(Boolean)
    }
  } catch (e) {
    console.error('Failed to parse Symbols component:', e)
    return {
      extends: 'P',
      text: this.call('polyglot', 'ai.chat.renderError').replace('{error}', e.message)
    }
  }
}

export function buildFallbackMessage(message) {
  return {
    extends: 'AIMessage',
    contentEditable: false,
    shape: 'bubble',
    shapeDirection: 'top right',
    round: 'C X C C',
    theme: 'dialog',
    padding: 'Y2 A',
    color: 'paragraph',
    maxWidth: 'I1',
    html: (el, s) =>
      typeof message === 'object' ? `<pre>${JSON.stringify(message, null, 2)}</pre>` : message
  }
}

export async function submitPrompt() {
  const el = this
  const s = this.state
  const value = s.keyword
  if (!value) return

  const images = s.images
  s.apply((s) => {
    s.thread.push({ role: 'user', message: value, images: [...images] })
    s.images = []
    s.keyword = ''
  })
  el.Flex.Span.node.innerText = ''

  // Add a temporary "thinking" message for better UX
  s.thread.push({ role: 'agent', isThinking: true, message: '...' })
  s.update()

  const selectedModel = s.model || 'Starter' // Default to Free if no model selected
  const res = await el.call('giveMeAnswer', value, images, selectedModel)
  const parsed = await el.call('parseAIAnswer', res)

  if (parsed?.asset?.type) {
    parsed.asset.type = el.call('getPlural', parsed.asset.type)
  }
  // Remove "thinking" message
  s.thread.pop()

  if (parsed?.asset && (parsed.asset.type === 'components' || parsed.asset.type === 'pages')) {
    try {
      const asset = parsed.asset
      const assetType = asset.type // 'components' or 'pages'

      // First, add the item to the main data store via the SDK
      const res = el.call('createItem', `${assetType}`, asset.key, asset)

      if (res) {
        // Add the AI response with both message and component code
        s.thread.push({
          role: 'agent',
          message: parsed.message || `I've created a ${asset.title} for you.`,
          analysis_text: asset.description,
          component_code: JSON.stringify(asset.value, null, 2),
          component: true
        })
      } else {
        // Handle cases where the SDK fails to add the item
        throw new Error(`Could not add ${assetType} '${asset.title}'`)
      }
    } catch (e) {
      console.error('Error adding or rendering component:', e)
      s.thread.push({
        role: 'agent',
        message: this.call('polyglot', 'ai.chat.addComponentError').replace('{error}', e.message)
      })
    }
  } else if (parsed.multiple) {
    const asset = parsed.asset
    const res = el.call('createMultipleItems', asset)

    if (res) {
      // Add the AI response with both message and component code
      s.thread.push({
        role: 'agent',
        message: parsed.message || `I've created a ${asset.title} for you.`,
        analysis_text: asset.description,
        component_code: JSON.stringify(asset.value, null, 2),
        component: true
      })
    }
  } else if (parsed) {
    // For other assets or text, display the information.
    s.thread.push({
      role: 'agent',
      analysis_text: parsed.message,
      component_code: parsed.asset ? JSON.stringify(parsed.asset, null, 2) : null,
      component: !!parsed.asset // Trigger the component renderer path
    })
  } else {
    s.thread.push({
      role: 'agent',
      message: this.call('polyglot', 'ai.chat.cannotProcess')
    })
  }
  if (s.activeContent !== 'AI') {
    s.update({ activeContent: 'AI' })
  }
}

/*
<!-- page -->
{
  key: '/about',
  title: 'About us page',
  type: 'page',
  description: '...',
  value: { ... },
}

<!-- component -->
{
  key: 'Header',
  title: 'Header component',
  type: 'components',
  description: '...',
  value: { ... },
}

<!-- function -->
{
  type: 'function',
  key: 'makeFn',
  title: 'Making function',
  description: '...',
  value: () => { ... },
}

<!-- method -->
{
  type: 'method',
  key: 'makeFn',
  title: 'Making method',
  description: '...',
  value: () => { ... },
}

<!-- dependency -->
{
  type: 'dependency',
  key: 'smbls',
  version: 'latest',
}

<!-- state -->
{
  type: 'state',
  key: 'products',
  value: [{ key: 'product1', ... }, { product2 }],
}

<!-- file -->
{
  type: 'file',
  key: 'some.jpg',
  value: 'url or base64', // need to check with @Thomas-sls about blob
}

<!-- color -->
{
  type: 'color',
  key: 'blue',
  value: '#123',
}

<!-- semantic colors (dark-light) -->
{
  type: 'color',
  key: 'call-to-action',
  value: {
    '@dark': '#123',
    '@light': '#321'
  },
}

<!-- icon -->
{
  type: 'icon',
  key: 'arrowRight',
  value: '<svg>...</svg>',
}

<!-- media query -->
{
  type: 'media',
  key: '@mobileXs',
  value: '(min-width: 100px)',
}

<!-- font -->
{
  type: 'font',
  key: 'FiraGO',
  value: [{
    // regular,
    url: 'https://cdn.jsdelivr.net/fontsource/fonts/firago@latest/latin-100-normal.woff2', // or base64,
    fontWeight: '400',
    fontStyle: 'normal',
    unicodeRange: 'latin',
  }, {
    // bold,
    url: 'https://cdn.jsdelivr.net/fontsource/fonts/firago@latest/latin-200-normal.woff2', // or base64,
    fontWeight: '700',
    fontStyle: 'normal',
    unicodeRange: 'latin',
  }],
}

<!-- font-family (often coupled with font) -->
{
  type: 'font_family',
  key: 'firaGo',
  key: 'FiraGO',
  familyName: 'FiraGO',
  type: 'serif',
  value: [
    'FiraGO', // maps with font name above
    'Helvetica, Arial, sans-serif, --system-default',
  ]
}

<!-- theme -->
{
  key: 'primary',
  type: 'theme',
  value: {
    color: '...',
    background: '...',
  }
}
*/
